// Shared Quick Actions buttons so multiple tables (Variants, Patient Details molecular)
// render identical direct-link actions for a single variant row.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { AssignmentInd, Handshake, QuestionAnswer } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import ReviewStatus from '../config/ReviewStatus.js';
import './QuickActionButtons.css';

// Quick Actions palette: left-edge accent bar color per action type.
export const ACTION_COLORS = {
  assigned: { accent: '#0f8b9e' },
  ready: { accent: '#c68a1f' },
  complete: { accent: '#4a8f5c' },
  newReview: { accent: '#7c3fc9' },
};

// Base state: 6px left accent bar (other borders come from the outlined variant).
// The global `button:focus-visible { border: none }` rule (index.css) collapses all borders and,
// with box-sizing: border-box, frees 7px of width that rewraps the label. Reassert the same border
// widths (transparent on the non-accent sides) on hover/focus so the box size never changes.
export const accentBarSx = (accent) => ({
  borderLeftWidth: '6px',
  borderLeftColor: accent,
  '& .MuiButton-startIcon': {
    color: accent,
  },
  '&:hover, &.Mui-focusVisible, &:focus': {
    borderStyle: 'solid',
    borderWidth: '1px',
    borderLeftWidth: '6px',
    borderColor: 'transparent',
    borderLeftColor: accent,
  },
  // Match the app's focus highlight: icon goes white on the orange focus background.
  '&.Mui-focusVisible .MuiButton-startIcon, &:focus .MuiButton-startIcon': {
    color: '#fff',
  },
});

const useStyles = makeStyles({
  // Stacked, full-width action buttons; text wraps inside.
  statusButton: {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: '5px 10px',
    lineHeight: 1.2,
    '& .MuiButton-startIcon': {
      marginTop: '-1px',
    },
    '& .statusButtonLabel': {
      whiteSpace: 'normal',
    },
  },
});

// Renders the Assigned-to-Me and consensus actions for a single variant row.
// `assignment` is this user's interpretation on the variant (or undefined).
// `assignedNavExtras` is merged into the Add Interpretation navigation state so
// callers can tag the origin (e.g. fromProgramSummary) without duplicating the button.
export default function QuickActionButtons({ row, assignment, breadCrumbs, programId, assignedNavExtras = {} }) {
  const classes = useStyles();
  const navigate = useNavigate();

  const goToConsensus = () => {
    navigate(`/variantconsensus/${row.variantId}`, {
      state: {
        variantId: row.variantId,
        reviewStatus: row.reviewStatus,
        variantName: row.variantName,
        gene: row.gene,
        potentialCondition: row.potentialCondition,
        breadCrumbs,
      },
    });
  };

  // Direct link to Add Interpretation for the current user's assignment on this variant.
  const assignedToMeButton = () => {
    // Hide entirely when there is no assignment or it is already complete.
    if (!assignment || assignment.status === 'Completed') {
      return null;
    }

    return (
      <Button
        className={classes.statusButton}
        variant="outlined"
        color="primary"
        aria-label="Add Interpretation Assigned to Me"
        startIcon={<AssignmentInd fontSize='small' />}
        sx={accentBarSx(ACTION_COLORS.assigned.accent)}
        onClick={() => navigate('/addinterpretation', {
          state: {
            breadCrumbs,
            programId,
            variantId: row.variantId,
            variantName: row.variantName,
            gene: row.gene,
            potentialCondition: row.potentialCondition,
            variantReviewId: assignment.variantReviewId,
            variantInterpretationId: assignment.variantInterpretationId,
            reviewStatus: row.reviewStatus,
            ...assignedNavExtras,
          },
        })}
      >
        <span className="statusButtonLabel">Assigned to Me</span>
      </Button>
    );
  };

  const consensusButton = () => {
    if (row.reviewStatus === ReviewStatus.CLOSED) {
      return (
        <Button
          className={classes.statusButton}
          variant="outlined"
          color="primary"
          aria-label="Consensus Complete"
          startIcon={<Handshake fontSize='small' />}
          sx={accentBarSx(ACTION_COLORS.complete.accent)}
          onClick={goToConsensus}
        >
          <span className="statusButtonLabel">Consensus Complete</span>
        </Button>
      );
    }

    if (row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS || row.reviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS) {
      const label = row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS
        ? 'Ready for Consensus'
        : 'Consensus in Progress';
      return (
        <Button
          className={classes.statusButton}
          variant="outlined"
          color="primary"
          aria-label={label}
          startIcon={<QuestionAnswer fontSize='small' />}
          sx={accentBarSx(ACTION_COLORS.ready.accent)}
          onClick={goToConsensus}
        >
          <span className="statusButtonLabel">{label}</span>
        </Button>
      );
    }

    return null;
  };

  const items = [
    <React.Fragment key="assigned">{assignedToMeButton()}</React.Fragment>,
    <React.Fragment key="consensus">{consensusButton()}</React.Fragment>,
  ].filter((item) => item.props.children);

  return <div className="statusCell">{items}</div>;
}
